import * as t from 'io-ts';
import _, {reduce, toPairs} from 'lodash';

import {ApplicationMessageTypesEnum} from '../../applications/bridge/v2/applicationBridgeV2';
import {makeApiType} from '../api/apiTypes';
import {RApplicationContextFunctions} from './applicationMessageModelFunctions';

/*
 * Runtime types.
 */

const RApplicationFunctionCallMessage = t.exact(
  t.type({
    type: t.literal(ApplicationMessageTypesEnum.FUNCTION_CALL),
    id: t.string,
    contextId: t.string,
    call: RApplicationContextFunctions,
  }),
);

const RApplicationFunctionCancelMessage = t.exact(
  t.type({
    type: t.literal(ApplicationMessageTypesEnum.FUNCTION_CANCEL),
    id: t.string,
  }),
);

// Union type.
const RApplicationMessageDirty = t.readonly(
  t.taggedUnion('type', [RApplicationFunctionCallMessage, RApplicationFunctionCancelMessage]),
);

/*
 * Types.
 */

export interface ApplicationContextFunctionDefinitions
  extends Readonly<t.TypeOf<typeof RApplicationContextFunctions>> {}
export interface ApplicationFunctionCallMessageModel
  extends Readonly<t.TypeOf<typeof RApplicationFunctionCallMessage>> {}
export interface ApplicationFunctionCancelMessageModel
  extends Readonly<t.TypeOf<typeof RApplicationFunctionCancelMessage>> {}

export type ApplicationMessageModel =
  | ApplicationFunctionCallMessageModel
  | ApplicationFunctionCancelMessageModel;
export const RApplicationMessage = t.clean<ApplicationMessageModel>(RApplicationMessageDirty as any);

const {types} = RApplicationContextFunctions.type;
export const applicationContextFunctionArities: {[K: string]: number | undefined} = _(types)
  .flatMap((value) => toPairs(value.props))
  .keyBy(([key]) => key)
  .mapValues(([_key, value]) => value.types.length)
  .value();

/*
 * IO methods.
 */

function parseApplicationMessage(src: any): ApplicationMessageModel {
  if (src.type === ApplicationMessageTypesEnum.FUNCTION_CALL) {
    return parseApplicationFunctionCallMessage(src);
  }

  return src;
}

function parseApplicationFunctionCallMessage(src: any): ApplicationFunctionCallMessageModel {
  // Restore function arities.
  const paddedCall = reduce(
    {[src.name]: src.args},
    (memo, functionArguments, functionName) => {
      // If there's no arity or if the arity is 0, let io-ts handle it.
      const arity = applicationContextFunctionArities[functionName];
      if (!arity || arity < functionArguments.length) {
        return {
          ...memo,
          [functionName]: functionArguments,
        };
      }

      // Otherwise pad the argument array with undefined values for the missing arguments.
      const missingArguments = new Array(arity - functionArguments.length);
      return {
        ...memo,
        [functionName]: [...functionArguments, ...missingArguments],
      };
    },
    {} as ApplicationContextFunctionDefinitions,
  );

  return {
    type: ApplicationMessageTypesEnum.FUNCTION_CALL,
    id: src.id,
    contextId: src.contextId,
    call: paddedCall,
  };
}

/*
 * API type.
 */

export const ApiApplicationMessage = makeApiType(
  'applicationMessage',
  RApplicationMessage,
  parseApplicationMessage,
);
